import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import Turnstile from "../Turnstile";
import type { TurnstileStatus } from "../Turnstile";
import { config } from "../../config";
import { ApiError, submitWaitlist } from "../../services/api";

import "./WaitlistModal.css";

// ---------------------------------------------------------------------------
// Validation schema -- mirrors src/api/schemas.py::WaitlistSubmitRequest and
// the bucket sets in src/api/routes/waitlist.py. If you add a bucket value,
// add it here AND in the route AND in the migration comment.
// ---------------------------------------------------------------------------
const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "ymail.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
]);

const USER_COUNT_OPTIONS = ["1-5", "6-25", "26-100", "100+"] as const;
const VERSION_OPTIONS = ["8.1.x", "8.3.x", "both", "other"] as const;

const ROLE_OPTIONS = [
  "Controls / Automation Engineer",
  "Integrator (consultant)",
  "DevOps / Platform Engineer",
  "QA / Test Engineer",
  "Engineering Manager",
  "Plant / Facilities Lead",
  "Other",
];

const INDUSTRY_OPTIONS = [
  "Oil & Gas",
  "Water / Wastewater",
  "Food & Beverage",
  "Pharma / Life Sciences",
  "Manufacturing (discrete)",
  "Manufacturing (process)",
  "Power & Utilities",
  "Mining / Metals",
  "Building Automation",
  "Other",
];

const schema = z.object({
  work_email: z
    .string()
    .trim()
    .min(1, "Work email is required.")
    .email("That doesn't look like a valid email.")
    .max(254)
    .refine(
      (v) => !FREE_EMAIL_DOMAINS.has(v.split("@")[1]?.toLowerCase() ?? ""),
      "Please use your work email so we can match you to your company.",
    ),
  company_name: z.string().trim().min(1, "Company name is required.").max(200),
  ignition_user_count: z.enum(USER_COUNT_OPTIONS, {
    errorMap: () => ({ message: "Pick a team-size bucket." }),
  }),
  user_role: z.string().trim().min(1, "Pick a role.").max(80),
  industry: z.string().trim().min(1, "Pick an industry.").max(80),
  ignition_version: z.enum(VERSION_OPTIONS, {
    errorMap: () => ({ message: "Pick an Ignition version." }),
  }),
});

type FormValues = z.infer<typeof schema>;

interface WaitlistModalProps {
  open: boolean;
  onClose: () => void;
  /** Free-form attribution slot. Defaults to where the user clicked from. */
  source?: string;
}

export default function WaitlistModal({ open, onClose, source }: WaitlistModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const [submitState, setSubmitState] = useState<
    | { kind: "idle" }
    | { kind: "submitting" }
    | { kind: "success"; emailSent: boolean }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [turnstileStatus, setTurnstileStatus] =
    useState<TurnstileStatus>("loading");
  const onTurnstileVerify = useCallback((token: string | null) => {
    setTurnstileToken(token);
  }, []);
  const onTurnstileStatus = useCallback((status: TurnstileStatus) => {
    setTurnstileStatus(status);
  }, []);

  const turnstileRequired = Boolean(config.turnstileSiteKey);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      work_email: "",
      company_name: "",
      ignition_user_count: undefined,
      user_role: "",
      industry: "",
      ignition_version: undefined,
    },
  });

  // Reset form + state when modal opens fresh.
  useEffect(() => {
    if (open) {
      previouslyFocusedRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      reset();
      setSubmitState({ kind: "idle" });
      setTurnstileToken(null);
      setTurnstileStatus("loading");
      setTurnstileAttempt(0);
      // Focus the first field after the modal mount tick.
      const id = window.setTimeout(() => firstFieldRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
    previouslyFocusedRef.current?.focus();
    previouslyFocusedRef.current = null;
    return undefined;
  }, [open, reset]);

  // Close on Escape; trap Tab inside the dialog (a11y).
  useEffect(() => {
    if (!open) return undefined;
    const dialog = dialogRef.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      // Build the focusable list each Tab so dynamic fields (e.g. error
      // banner, Turnstile widget) are included as soon as they appear.
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
          'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitState({ kind: "submitting" });
    try {
      const result = await submitWaitlist({
        ...values,
        source,
        turnstile_token: turnstileToken ?? undefined,
      });
      setSubmitState({ kind: "success", emailSent: result.email_sent });
    } catch (e) {
      let message = "Something went wrong. Please try again.";
      if (e instanceof ApiError) {
        message = e.message;
      }
      setSubmitState({ kind: "error", message });
    }
  });

  if (!open) return null;

  // The native register() spreads first, then we override `ref` so we can
  // also focus the first field when the modal opens.
  const emailField = register("work_email");

  return (
    <div
      className="oc-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="oc-modal-title"
        className="oc-modal"
      >
        <button
          type="button"
          className="oc-modal-close"
          onClick={onClose}
          aria-label="Close waitlist form"
        >
          &times;
        </button>

        {submitState.kind === "success" ? (
          <SuccessPanel emailSent={submitState.emailSent} onClose={onClose} />
        ) : (
          <>
            <h2 id="oc-modal-title" className="oc-modal-title">
              Join the {config.brandName} waitlist
            </h2>
            <p className="oc-modal-subtitle">
              30 seconds, six fields. We&rsquo;ll email you when access opens.
            </p>

            <form className="oc-modal-form" onSubmit={onSubmit} noValidate>
              <Field label="Work email" error={errors.work_email?.message}>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  {...emailField}
                  ref={(el) => {
                    emailField.ref(el);
                    firstFieldRef.current = el;
                  }}
                />
              </Field>

              <Field label="Company name" error={errors.company_name?.message}>
                <input
                  type="text"
                  autoComplete="organization"
                  placeholder="Acme Integration"
                  {...register("company_name")}
                />
              </Field>

              <div className="oc-modal-row">
                <Field label="Ignition users" error={errors.ignition_user_count?.message}>
                  <select {...register("ignition_user_count")} defaultValue="">
                    <option value="" disabled>
                      Pick a range
                    </option>
                    {USER_COUNT_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Ignition version" error={errors.ignition_version?.message}>
                  <select {...register("ignition_version")} defaultValue="">
                    <option value="" disabled>
                      Pick a version
                    </option>
                    <option value="8.1.x">8.1.x</option>
                    <option value="8.3.x">8.3.x</option>
                    <option value="both">Both 8.1 and 8.3</option>
                    <option value="other">Other / earlier</option>
                  </select>
                </Field>
              </div>

              <Field label="Your role" error={errors.user_role?.message}>
                <select {...register("user_role")} defaultValue="">
                  <option value="" disabled>
                    Pick a role
                  </option>
                  {ROLE_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Industry" error={errors.industry?.message}>
                <select {...register("industry")} defaultValue="">
                  <option value="" disabled>
                    Pick an industry
                  </option>
                  {INDUSTRY_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </Field>

              {turnstileRequired && (
                <div className="oc-modal-turnstile">
                  <Turnstile
                    key={turnstileAttempt}
                    onVerify={onTurnstileVerify}
                    onStatus={onTurnstileStatus}
                  />
                  {turnstileStatus === "loading" && (
                    <p className="oc-modal-turnstile-status" role="status">
                      Loading bot check&hellip;
                    </p>
                  )}
                  {turnstileStatus === "ready" && !turnstileToken && (
                    <p className="oc-modal-turnstile-status">
                      Complete the bot check to continue.
                    </p>
                  )}
                  {(turnstileStatus === "error" || turnstileStatus === "expired") && (
                    <div className="oc-modal-turnstile-recovery" role="alert">
                      <span>
                        {turnstileStatus === "expired"
                          ? "The bot check expired."
                          : "The bot check could not load."}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setTurnstileToken(null);
                          setTurnstileStatus("loading");
                          setTurnstileAttempt((attempt) => attempt + 1);
                        }}
                      >
                        Retry bot check
                      </button>
                    </div>
                  )}
                </div>
              )}

              {submitState.kind === "error" && (
                <div className="oc-modal-error" role="alert">
                  {submitState.message}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-large oc-modal-submit"
                disabled={
                  isSubmitting ||
                  submitState.kind === "submitting" ||
                  (turnstileRequired && !turnstileToken)
                }
              >
                {submitState.kind === "submitting" || isSubmitting
                  ? "Submitting..."
                  : "Join the waitlist"}
              </button>

              <p className="oc-modal-microcopy">
                By joining the waitlist you agree to receive product emails from{" "}
                {config.brandName}. We&rsquo;ll only email you about access and
                pricing. Unsubscribe anytime. See our{" "}
                <a href="/privacy" target="_blank" rel="noreferrer">
                  privacy policy
                </a>
                .
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
interface FieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, error, children }: FieldProps) {
  return (
    <label className={`oc-field ${error ? "is-invalid" : ""}`}>
      <span className="oc-field-label">{label}</span>
      {children}
      {error && <span className="oc-field-error">{error}</span>}
    </label>
  );
}

interface SuccessPanelProps {
  emailSent: boolean;
  onClose: () => void;
}

function SuccessPanel({ emailSent, onClose }: SuccessPanelProps) {
  return (
    <div className="oc-modal-success">
      <div className="oc-modal-success-icon" aria-hidden="true">
        &#x2713;
      </div>
      <h2 id="oc-modal-title">You&rsquo;re on the list</h2>
      <p>
        {emailSent
          ? "Check your inbox for a confirmation. We'll email you when early access opens up."
          : "We've got your details. We'll email you when early access opens up."}
      </p>
      <button type="button" className="btn btn-outline btn-large" onClick={onClose}>
        Close
      </button>
    </div>
  );
}
